import { useState, useEffect, createContext, useContext } from 'react';

import { getFeatureFlags } from '@/resources/feature-flag-service.resource';

const FeatureFlagContext = createContext({});

export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}

export function FeatureFlagProvider({ children }) {
  const [featureFlags, setFeatureFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getFlags = async () => {
      let globalFlags = {};
      try {
        const flagsList = await getFeatureFlags();
        // Create an object from the feature flags array.
        globalFlags =
          flagsList?.data?.reduce(
            (acc, item) => ({ ...acc, [item.feature]: item.isEnabled }),
            {}
          ) ?? {};
      } catch {
        // Feature-flag service unreachable (e.g. local dev with no flag service).
        // Don't crash the app — fall back to local overrides only.
        globalFlags = {};
      }
      // Local overrides set via the Feature Flag modal (localStorage).
      let localOverrides = {};
      try {
        if (typeof window !== 'undefined') {
          localOverrides = JSON.parse(
            window.localStorage.getItem('core-feature-flags') ?? '{}'
          );
        }
      } catch {
        localOverrides = {};
      }
      // Env override (e.g. NEXT_PUBLIC_FEATURE_FLAGS_OVERRIDE='{"EnableDonations":true}') —
      // a reliable local/dev switch that survives reloads and localStorage clears, and
      // needs no feature-flag service. Unset (and thus inert) outside local/dev.
      let envOverrides = {};
      try {
        envOverrides = JSON.parse(process.env.NEXT_PUBLIC_FEATURE_FLAGS_OVERRIDE || '{}');
      } catch {
        envOverrides = {};
      }
      setFeatureFlags({ ...globalFlags, ...localOverrides, ...envOverrides });
      setIsLoading(false);
    };
    getFlags();
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ featureFlags, isLoading, setFeatureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}
